import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { useTranslation } from 'react-i18next'
import {
	apiRejectOnboarding,
	apiUpdateOnboarding,
} from '../../services/api/onboarding'
import {
	apiGetDistrictList,
	apiGetProvinceList,
	apiGetSubDistrictList,
} from '@ms/shared/services/api/masterdata'
import { apiGetPublicOrganizeGroups } from '../../services/api/social_group'
import { getApiErrorMessage } from '../../services/api/error'
import { redirectToSocial } from '../../services/social_handoff'
import { getRoleNameKey } from '../../utils/role_name_th'
import mascot from '../../assets/images/login-mascot.png'
import AuthHeaderActions from './auth_header_actions'
import CancelApplicationConfirmDialog from './cancel_application_confirm_dialog'
import {
	clearAuthSession,
	getToken,
	getUserInfo,
} from '../../services/auth_storage'
import {
	clearOnboardingSession,
	getOnboardingSession,
} from '../../services/onboarding_storage'
import './style/student_onboarding.css'

const toProvinceOptions = (provinces = []) => provinces
	.map(({ province_id, province_name_th }) => ({
		label: province_name_th,
		value: String(province_id).trim(),
	}))
	.sort((left, right) => left.label.localeCompare(right.label, 'th'))

const toDistrictOptions = (districts = []) => districts
	.map(({ district_id, district_name_th }) => ({
		label: district_name_th,
		value: String(district_id).trim(),
	}))
	.sort((left, right) => left.label.localeCompare(right.label, 'th'))

const toSubdistrictOptions = (subdistricts = []) => subdistricts
	.map(({ sub_district_id, sub_district_name_th }) => ({
		label: sub_district_name_th,
		value: String(sub_district_id).trim(),
	}))
	.sort((left, right) => left.label.localeCompare(right.label, 'th'))

const groupAvatarPrefix = 'data:image/jpeg;base64,'

function StudentOnboarding() {
	const { t } = useTranslation()
	const onboardingSession = useMemo(getOnboardingSession, [])
	const onboardingGroupId = onboardingSession?.data?.group_id || null
	const [schools, setSchools] = useState([])
	const [provinceOptions, setProvinceOptions] = useState([])
	const [districtOptions, setDistrictOptions] = useState([])
	const [subdistrictOptions, setSubdistrictOptions] = useState([])
	const [query, setQuery] = useState('')
	const [selectedSchool, setSelectedSchool] = useState(null)
	const [showAll, setShowAll] = useState(false)
	const [province, setProvince] = useState(null)
	const [district, setDistrict] = useState(null)
	const [subdistrict, setSubdistrict] = useState(null)
	const [coordinates, setCoordinates] = useState(null)
	const [locating, setLocating] = useState(false)
	const [loading, setLoading] = useState(true)
	const [submitting, setSubmitting] = useState('')
	const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false)
	const [error, setError] = useState('')

	const userInfo = useMemo(getUserInfo, [])

	const firstName = userInfo.first_name || userInfo.firstname || userInfo.name || t('onboarding.student.defaultName')
	const profileImage = userInfo.profile_picture || userInfo.profile_image || userInfo.avatar
	const roleName = t(getRoleNameKey(userInfo.role_id) || 'onboarding.student.role')
	const provinceName = provinceOptions.find((option) => option.value === province)?.label || ''
	const districtName = districtOptions.find((option) => option.value === district)?.label || ''
	const subdistrictName = subdistrictOptions.find((option) => option.value === subdistrict)?.label || ''
	const visibleSchools = showAll || query.trim() ? schools : schools.slice(0, 3)

	useEffect(() => {
		if (!getToken() || onboardingSession?.roleId !== 2) {
			window.location.replace('/login')
		}
	}, [onboardingSession])

	useEffect(() => {
		let active = true
		const loadProvinces = async () => {
			try {
				const response = await apiGetProvinceList()
				if (!Array.isArray(response?.data)) {
					throw new Error(t('onboarding.student.errors.loadSchools'))
				}
				if (active) setProvinceOptions(toProvinceOptions(response.data))
			} catch (loadError) {
				if (active) {
					setError(getApiErrorMessage(loadError, t('onboarding.student.errors.loadSchools')))
				}
			}
		}

		loadProvinces()
		return () => {
			active = false
		}
	}, [t])

	useEffect(() => {
		let active = true
		setDistrictOptions([])
		if (!province) return () => { active = false }

		const loadDistricts = async () => {
			try {
				const response = await apiGetDistrictList(province)
				if (!Array.isArray(response?.data)) {
					throw new Error(t('onboarding.student.errors.loadSchools'))
				}
				if (active) setDistrictOptions(toDistrictOptions(response.data))
			} catch (loadError) {
				if (active) {
					setError(getApiErrorMessage(loadError, t('onboarding.student.errors.loadSchools')))
				}
			}
		}

		loadDistricts()
		return () => {
			active = false
		}
	}, [province, t])

	useEffect(() => {
		let active = true
		setSubdistrictOptions([])
		if (!province || !district) return () => { active = false }

		const loadSubdistricts = async () => {
			try {
				const response = await apiGetSubDistrictList(province, district)
				if (!Array.isArray(response?.data)) {
					throw new Error(t('onboarding.student.errors.loadSchools'))
				}
				if (active) setSubdistrictOptions(toSubdistrictOptions(response.data))
			} catch (loadError) {
				if (active) {
					setError(getApiErrorMessage(loadError, t('onboarding.student.errors.loadSchools')))
				}
			}
		}

		loadSubdistricts()
		return () => {
			active = false
		}
	}, [district, province, t])

	useEffect(() => {
		let active = true
		const loadGroups = async () => {
			setLoading(true)
			setError('')
			try {
				const params = {
					sort_by: coordinates ? 'distance' : 'name',
					sort_direction: 'ASC',
					limit: 100,
					offset: 0,
				}

				if (query.trim()) params.name = query.trim()
				if (provinceName) params.province_name = provinceName
				if (districtName) params.district_name = districtName
				if (subdistrictName) params.sub_district_name = subdistrictName
				if (coordinates) {
					params.latitude = coordinates.latitude
					params.longtitude = coordinates.longitude
				}

				const response = await apiGetPublicOrganizeGroups(params)
				const groups = Array.isArray(response?.data?.groups) ? response.data.groups : []
				if (!active) return

				setSchools(groups)
				setSelectedSchool((current) => {
					if (current && groups.some((group) => group.id === current.id)) return current
					return groups.find((group) => group.id === onboardingGroupId) || null
				})
			} catch (loadError) {
				if (active) {
					setSchools([])
					setError(getApiErrorMessage(loadError, t('onboarding.student.errors.loadSchools')))
				}
			} finally {
				if (active) setLoading(false)
			}
		}

		const timeoutId = window.setTimeout(loadGroups, 300)
		return () => {
			active = false
			window.clearTimeout(timeoutId)
		}
	}, [coordinates, districtName, onboardingGroupId, provinceName, query, subdistrictName, t])

	const selectProvince = (value) => {
		setProvince(value)
		setDistrict(null)
		setSubdistrict(null)
		setDistrictOptions([])
		setSubdistrictOptions([])
		setCoordinates(null)
	}

	const selectDistrict = (value) => {
		setDistrict(value)
		setSubdistrict(null)
		setSubdistrictOptions([])
		setCoordinates(null)
	}

	const selectSubdistrict = (value) => {
		setSubdistrict(value)
		setCoordinates(null)
	}

	const completeOnboarding = async (groupId) => {
		const response = await apiUpdateOnboarding(
			groupId ? { group_id: groupId } : {},
			{ currentFormStep: 1 },
		)
		if (response?.status < 200 || response?.status >= 300) {
			throw new Error(response?.data?.message || t('onboarding.common.errors.save'))
		}
		clearOnboardingSession()
		await redirectToSocial(groupId)
	}

	const continueToApplication = async () => {
		if (!selectedSchool || submitting) return
		setSubmitting('continue')
		setError('')
		try {
			await completeOnboarding(selectedSchool.id)
		} catch (submitError) {
			setError(getApiErrorMessage(submitError, t('onboarding.common.errors.continue')))
			setSubmitting('')
		}
	}

	const skipOnboarding = async () => {
		if (submitting) return
		setSubmitting('skip')
		setError('')
		try {
			await completeOnboarding()
		} catch (submitError) {
			setError(getApiErrorMessage(submitError, t('onboarding.common.errors.skip')))
			setSubmitting('')
		}
	}

	const useCurrentLocation = () => {
		if (!navigator.geolocation || locating) return
		setLocating(true)
		navigator.geolocation.getCurrentPosition(
			(position) => {
				setCoordinates({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
				})
				setProvince(null)
				setDistrict(null)
				setSubdistrict(null)
				setQuery('')
				setShowAll(true)
				setLocating(false)
			},
			() => {
				setError(t('onboarding.student.errors.location'))
				setLocating(false)
			},
			{ enableHighAccuracy: false, timeout: 6000 },
		)
	}

	const cancelApplication = async () => {
		if (submitting) return
		setSubmitting('cancel')
		setError('')
		try {
			const response = await apiRejectOnboarding()
			if (response?.status < 200 || response?.status >= 300) {
				throw new Error(response?.data?.message || t('onboarding.common.errors.cancelShort'))
			}
			clearAuthSession()
			clearOnboardingSession()
			window.location.assign('/login?logout=true&application_cancelled=true')
		} catch (cancelError) {
			setError(getApiErrorMessage(cancelError, t('onboarding.common.errors.cancel')))
			setSubmitting('')
			setCancelConfirmVisible(false)
		}
	}

	const hideCancelConfirm = () => {
		if (!submitting) setCancelConfirmVisible(false)
	}

	return (
		<main className='student-onboarding-page'>
			<CancelApplicationConfirmDialog
				visible={cancelConfirmVisible}
				loading={submitting === 'cancel'}
				onAccept={cancelApplication}
				onHide={hideCancelConfirm}
			/>
			<header className='student-onboarding-header'>
				<div className='student-onboarding-brand' aria-label={t('onboarding.common.brandAriaLabel')}>
					<span className='student-onboarding-logo'>{t('onboarding.common.logo')}</span>
					<span>{t('onboarding.common.education')} <strong>{t('onboarding.common.flexible')}</strong></span>
				</div>
				<div className='student-onboarding-header-actions'>
					<AuthHeaderActions />
					<div className='student-onboarding-user'>
						<span className='student-onboarding-avatar'>
							{profileImage ? <img src={profileImage} alt='' /> : '👦🏻'}
						</span>
						<span className='student-onboarding-user-copy'>{t('onboarding.common.greeting', { name: firstName })}<small>{roleName}</small></span>
						<i className='pi pi-bars student-onboarding-mobile-user-icon' aria-hidden='true' />
					</div>
				</div>
			</header>

			<section className='student-onboarding-shell'>
				<div className='student-onboarding-intro'>
					<div className='student-onboarding-welcome'>
						<h1>{t('onboarding.common.welcome')}<br />{t('onboarding.common.education')}<strong>{t('onboarding.common.flexible')}</strong></h1>
						<p>{t('onboarding.common.welcomeDescription')}<br />{t('onboarding.student.welcomeInstruction')}</p>
					</div>
					<img src={mascot} alt={t('onboarding.common.mascotAlt')} />
				</div>

				<div className='student-onboarding-panel'>
					<div className='student-onboarding-panel-content'>
						<p className='student-onboarding-role'>{roleName}</p>
						<h1>{t('onboarding.student.title')}</h1>
						<p className='student-onboarding-subtitle'>{t('onboarding.student.subtitle')}</p>

						<section className='student-onboarding-location-section' aria-labelledby='student-location-title'>
							<strong id='student-location-title' className='student-onboarding-location-title'>{t('onboarding.student.searchByLocation')}</strong>
							<div className='student-onboarding-location-grid'>
								<label>
									<span>{t('onboarding.student.subdistrict')}</span>
									<Dropdown value={subdistrict} onChange={(event) => selectSubdistrict(event.value)} options={subdistrictOptions} placeholder={t('onboarding.student.selectSubdistrict')} showClear disabled={!district} aria-label={t('onboarding.student.subdistrictAriaLabel')} />
								</label>
								<label>
									<span>{t('onboarding.student.district')}</span>
									<Dropdown value={district} onChange={(event) => selectDistrict(event.value)} options={districtOptions} placeholder={t('onboarding.student.selectDistrict')} showClear disabled={!province} aria-label={t('onboarding.student.districtAriaLabel')} />
								</label>
								<label>
									<span>{t('onboarding.student.province')}</span>
									<Dropdown value={province} onChange={(event) => selectProvince(event.value)} options={provinceOptions} placeholder={t('onboarding.student.selectProvince')} showClear aria-label={t('onboarding.student.province')} />
								</label>
								<Button
									type='button'
									outlined
									icon={locating ? 'pi pi-spin pi-spinner' : 'pi pi-map-marker'}
									label={t(coordinates ? 'onboarding.student.locationApplied' : 'onboarding.student.useCurrentLocation')}
									onClick={useCurrentLocation}
									disabled={locating}
									className={`student-onboarding-location-button${coordinates ? ' active' : ''}`}
								/>
							</div>

							<div className='student-onboarding-search-row'>
								<span className='p-input-icon-left student-onboarding-search'>
									<i className='pi pi-search' />
									<InputText value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('onboarding.student.searchSchool')} aria-label={t('onboarding.student.searchSchool')} />
								</span>
								{/* Keep the institution-type filter hidden until this filter is supported. */}
								{/* <Dropdown value='institution' options={[{ label: t('onboarding.student.all'), value: 'institution' }]} className='student-onboarding-filter' disabled aria-label={t('onboarding.student.institutionType')} /> */}
							</div>
						</section>

						<p className='student-onboarding-list-title'>{t(coordinates ? 'onboarding.student.nearbySchools' : 'onboarding.student.recommended')}</p>
						<div className={`student-onboarding-list${showAll ? ' expanded' : ''}`} aria-live='polite'>
							{loading ? <div className='student-onboarding-loading'>{t('onboarding.student.loadingSchools')}</div> : visibleSchools.length > 0 ? visibleSchools.map((school, index) => {
								const selected = selectedSchool?.id === school.id
								return (
									<button type='button' className={`student-onboarding-item${selected ? ' selected' : ''}`} key={school.id} onClick={() => setSelectedSchool(school)} aria-pressed={selected}>
										<span className='student-onboarding-radio' />
										<span className={`student-onboarding-emblem emblem-${index % 5}`}>
											{school.avatar_photo
												? <img src={`${groupAvatarPrefix}${school.avatar_photo}`} alt='' />
												: school.name?.slice(0, 2) || index + 1}
										</span>
										<span className='student-onboarding-school-copy'><strong>{school.name}</strong><small>{[school.sub_district_name, school.district_name, school.province_name].filter(Boolean).join(', ') || t('onboarding.student.locationUnspecified')}</small></span>
										<i className='pi pi-angle-right' aria-hidden='true' />
									</button>
								)
							}) : <div className='student-onboarding-empty'>{error || t('onboarding.student.noSchoolsFound')}</div>}
						</div>
					</div>

					{schools.length > 3 && !query && (
						<div className='student-onboarding-more-row'>
							<button type='button' className='student-onboarding-more' onClick={() => setShowAll((current) => !current)}>
								{t(showAll ? 'onboarding.student.showLess' : 'onboarding.student.showMore')} <i className={`pi pi-chevron-${showAll ? 'up' : 'down'}`} />
							</button>
						</div>
					)}

					<div className='student-onboarding-actions'>
						<Button type='button' outlined icon={submitting === 'skip' ? 'pi pi-spin pi-spinner' : 'pi pi-forward'} aria-label={t('onboarding.student.actions.skipAriaLabel')} onClick={skipOnboarding} disabled={Boolean(submitting)} className='student-onboarding-skip'>
							<span className='student-onboarding-action-copy'><strong>{t('onboarding.common.actions.skip')}</strong><small>{t('onboarding.student.actions.skipDescription')}</small></span>
						</Button>
						<Button type='button' icon={submitting === 'continue' ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'} iconPos='right' aria-label={t('onboarding.common.actions.continueAriaLabel')} disabled={!selectedSchool || Boolean(submitting)} onClick={continueToApplication} className='student-onboarding-continue'>
							<span className='student-onboarding-action-copy'><strong>{t('onboarding.common.actions.continue')}</strong><small>{t('onboarding.common.actions.toApplication')}</small></span>
						</Button>
						<Button type='button' outlined icon='pi pi-trash' aria-label={t('onboarding.student.actions.cancelAriaLabel')} onClick={() => setCancelConfirmVisible(true)} disabled={Boolean(submitting)} className='student-onboarding-cancel'>
							<span className='student-onboarding-action-copy'><strong>{t('onboarding.common.actions.cancel')}</strong><small>{t('onboarding.student.actions.cancelDescription')}</small></span>
						</Button>
					</div>
					<div className='student-onboarding-note' role={error ? 'alert' : undefined}><i className={`pi ${error ? 'pi-exclamation-circle' : 'pi-info-circle'}`} /><span>{error || t('onboarding.student.note')}</span></div>
				</div>
			</section>
		</main>
	)
}

export default StudentOnboarding
